/**
 * Generic entity-translation editing (admin/translation).
 *
 * ONE service for EVERY translatable entity type and EVERY locale. That is the whole point: the
 * server exposes `/schema/{type}` describing which fields and which locales exist, so neither this
 * module nor the UI above it names a locale, a field or a type anywhere. Adding a 10th language is
 * a `supported_locale` row on the server — no change here, no change in the panel.
 *
 * TWO mounts, picked per persona by {@link translationScope} — a company admin / trainer talks to
 * `/admin/translation/**`, a super-admin to the identical `/system/translation/**`. Getting that
 * dispatch wrong is not a cosmetic bug: see the note on `translationScope`.
 *
 *   GET    {admin,system}/translation/schema/{type}         fields + locales for a type
 *   GET    {admin,system}/translation/{type}/{id}           every locale + canonical + staleness
 *   PUT    {admin,system}/translation/{type}/{id}/{locale}  upsert/clear one locale's fields
 *   DELETE {admin,system}/translation/{type}/{id}/{locale}  clear a whole locale
 *
 * Two server semantics this module exists to make explicit, because getting them wrong is silent:
 *
 *  1. **A blank value in the PUT body CLEARS that field** (soft-delete), so the reader falls back
 *     through the locale chain instead of rendering an empty label. "No change" is therefore
 *     expressed by OMITTING the key, never by sending "". `saveTranslations` passes the map through
 *     untouched — building that map correctly is the caller's job (see useEntityTranslations).
 *  2. **The canonical locale can never be written or cleared here.** It is the terminus of every
 *     fallback chain AND is mirrored into a NOT NULL column, so the server refuses it. Canonical
 *     text is edited through the entity's own form field, which writes the derived column.
 *
 * Unlike the other services this one surfaces the HTTP STATUS, not just a message. A 403 is a
 * first-class outcome: on the COMPANY path `@authz.canTranslate` deliberately fails closed for
 * TEMPLATE and TEMPLATE_WORKOUT (their legacy tables have no safe ownership path), so "this type
 * is not translatable here" is a state the panel renders, not an error it reports. (That is a
 * company-path rule only — SystemTranslationController's gate is the class-level
 * @IsSystemAdminPrincipal with no per-row check.)
 */

import {
    adminTranslationControllerSchema,
    adminTranslationControllerGet,
    adminTranslationControllerSave,
    adminTranslationControllerClear,
    systemTranslationControllerSchema,
    systemTranslationControllerGet,
    systemTranslationControllerSave,
    systemTranslationControllerClear,
} from '../api/generated';
import type {
    AdminTranslationControllerSchemaData,
    TranslationSchema,
    EntityTranslations,
    WriteResult,
} from '../api/generated/types.gen';
import { isSuperAdminAuthority, isCompanyGroupAdminAuthority } from '../contexts/AuthContext';

/**
 * The registered translatable types, taken from the generated path parameter rather than written
 * out — so a type added to the server's `TranslatableType` enum appears here on the next
 * `gen:api:live`, and a type removed becomes a compile error at the call site.
 */
export type TranslatableTypeCode = AdminTranslationControllerSchemaData['path']['type'];

export type { TranslationSchema, EntityTranslations, WriteResult };

/** Field key -> value. A blank value CLEARS; an absent key means "leave as it is". */
export type TranslationValues = Record<string, string>;

export type TranslationResult<T> =
    | { ok: true; data: T }
    | { ok: false; status?: number; message: string; reason?: 'unsupported-persona' };

/**
 * Which mount of this surface the current persona may use.
 *
 * **This is the bug that shipped first:** every call went to `/admin/translation/**`, which is
 * floored at `hasAnyAuthority('ADMIN','TRAINER')` — a floor that DELIBERATELY excludes
 * SYSTEM_ADMIN, because a super-admin has no company and reaches company-less content through
 * `/system/**`. So a super-admin editing a system exercise could change its Hebrew name (that call
 * already dispatched correctly, via ExcersiceService) but got a 403 on every translation call. The
 * panel then rendered its fail-closed state and looked like the server refusing the ENTITY, when
 * in fact the client had simply knocked on the wrong door.
 *
 * The invariant this restores, and the one to hold onto: **wherever a persona can edit the
 * canonical text, that same persona can translate it.** Translation authorization follows the
 * entity, so the client's only job is picking the mount that matches the persona — exactly what
 * `ExcersiceService` does for `/admin/exercise` vs `/system/exercise`.
 *
 * Read from the DB-derived authorities rather than the JWT `roles` claim, for the reason
 * `isSuperAdminAuthority` documents: a token minted before that claim existed is still valid for
 * up to 180 days, and a JWT-only check would misclassify a real SYSTEM_ADMIN — i.e. reintroduce
 * this exact bug for the oldest sessions only, which is the worst possible way to have it.
 *
 * Impersonation needs no case of its own: `AuthProvider` mirrors the IMPERSONATED identity's
 * authorities, so an impersonating super-admin takes the company path, which is correct — the
 * server scopes by principal, and an impersonated company admin's edit forks a company-local copy
 * exactly as a direct login's would.
 */
export type TranslationScope = 'system' | 'company' | 'unsupported';

export const translationScope = (): TranslationScope => {
    if (isSuperAdminAuthority()) return 'system';
    // A COMPANY_GROUP_ADMIN genuinely CAN edit the canonical Hebrew of its own library (through
    // the owner-scoped /groupAdmin/* content endpoints), so by the invariant above it should be
    // able to translate it — but there is no `/groupAdmin/translation` controller on the server
    // yet. The honest answer is to say so and send NO request:
    //   - /admin would 403 (no company scope) and render "this item cannot be translated here",
    //     which blames the item for a missing endpoint and is the wrong diagnosis;
    //   - /system would be a request for someone else's privileges, and is refused for good reason.
    // When the controller lands, this branch is deleted and a `groupAdmin` case replaces it.
    if (isCompanyGroupAdminAuthority()) return 'unsupported';
    return 'company';
};

/** The one place the group-admin gap is reported, so the copy cannot drift between the four calls. */
const UNSUPPORTED: TranslationResult<never> = {
    ok: false,
    reason: 'unsupported-persona',
    message: 'ניהול תרגומים אינו זמין עדיין עבור ספריית מנהל קבוצת החברות',
};

/**
 * The generated fetch client reports a non-2xx on `error` and the status on `response`. `error` is
 * often an empty body (Spring's 403 has none), so the STATUS is the reliable signal and the message
 * is best-effort.
 */
const failure = (
    error: unknown,
    response: Response | undefined,
    fallback: string,
): TranslationResult<never> => {
    const maybe = error as { message?: string; errorMessage?: string } | undefined;
    return {
        ok: false,
        status: response?.status,
        message: maybe?.errorMessage || maybe?.message || fallback,
    };
};

const thrown = (error: unknown, fallback: string): TranslationResult<never> => {
    const maybe = error as
        | { status?: number; response?: { status?: number; data?: { message?: string } }; message?: string }
        | undefined;
    return {
        ok: false,
        status: maybe?.status ?? maybe?.response?.status,
        message: maybe?.response?.data?.message || maybe?.message || fallback,
    };
};

/** Which fields and which locales exist for a type. The UI renders itself from this. */
export const getTranslationSchema = async (
    type: TranslatableTypeCode,
): Promise<TranslationResult<TranslationSchema>> => {
    const scope = translationScope();
    if (scope === 'unsupported') return UNSUPPORTED;
    try {
        const call =
            scope === 'system' ? systemTranslationControllerSchema : adminTranslationControllerSchema;
        const { data, error, response } = await call({ path: { type } });
        if (error || !data?.body) {
            return failure(error, response, 'שגיאה בטעינת הגדרות התרגום');
        }
        return { ok: true, data: data.body };
    } catch (e) {
        return thrown(e, 'שגיאה בטעינת הגדרות התרגום');
    }
};

/** Every locale for one row, plus the canonical text to translate FROM and a staleness flag. */
export const getEntityTranslations = async (
    type: TranslatableTypeCode,
    id: number,
): Promise<TranslationResult<EntityTranslations>> => {
    const scope = translationScope();
    if (scope === 'unsupported') return UNSUPPORTED;
    try {
        const call =
            scope === 'system' ? systemTranslationControllerGet : adminTranslationControllerGet;
        const { data, error, response } = await call({ path: { type, id } });
        if (error || !data?.body) {
            return failure(error, response, 'שגיאה בטעינת התרגומים');
        }
        return { ok: true, data: data.body };
    } catch (e) {
        return thrown(e, 'שגיאה בטעינת התרגומים');
    }
};

/**
 * Upsert or clear one locale's fields.
 *
 * `values` must contain ONLY the fields that changed: a key with a blank value clears that field,
 * and a key that is absent is left alone. Never pass the whole field set with blanks for the
 * untouched ones — that would wipe translations the user never looked at.
 */
export const saveTranslations = async (
    type: TranslatableTypeCode,
    id: number,
    locale: string,
    values: TranslationValues,
): Promise<TranslationResult<WriteResult>> => {
    const scope = translationScope();
    if (scope === 'unsupported') return UNSUPPORTED;
    try {
        const call =
            scope === 'system' ? systemTranslationControllerSave : adminTranslationControllerSave;
        const { data, error, response } = await call({
            path: { type, id, locale },
            body: values,
        });
        if (error || !data?.body) {
            return failure(error, response, 'שגיאה בשמירת התרגום');
        }
        return { ok: true, data: data.body };
    } catch (e) {
        return thrown(e, 'שגיאה בשמירת התרגום');
    }
};

/** Clear every field of one locale, reverting that locale to the fallback chain. */
export const clearTranslations = async (
    type: TranslatableTypeCode,
    id: number,
    locale: string,
): Promise<TranslationResult<WriteResult>> => {
    const scope = translationScope();
    if (scope === 'unsupported') return UNSUPPORTED;
    try {
        const call =
            scope === 'system' ? systemTranslationControllerClear : adminTranslationControllerClear;
        const { data, error, response } = await call({
            path: { type, id, locale },
        });
        if (error || !data?.body) {
            return failure(error, response, 'שגיאה במחיקת התרגום');
        }
        return { ok: true, data: data.body };
    } catch (e) {
        return thrown(e, 'שגיאה במחיקת התרגום');
    }
};
